import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSubModules } from "../../../Store/moduleSlice";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  FiDatabase,
  FiTrendingUp,
  FiShield,
  FiBarChart2,
  FiChevronRight,
  FiLayers,
  FiLock,
  FiActivity,
  FiArrowRight,
  FiStar,
  FiCpu,
  FiZap,
} from "react-icons/fi";
import informationTechnology from "../../../assets/infoandtech.png";
import forecast from "../../../assets/forecast.png";
import accounting from "../../../assets/accounting.png";
import procureToPay from "../../../assets/procurePay.png";
import orderToCash from "../../../assets/orderToCash.png";
import machine from "../../../assets/packing-machine.png";

const SubModulePage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  const moduleName = location.state?.moduleName;

  const { subModules, loading, error } = useSelector((state) => state.module);

  useEffect(() => {
    if (moduleName) {
      dispatch(fetchSubModules(moduleName));
    }
  }, [moduleName, dispatch]);

  const formatName = (name) => {
    return name
      ?.toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleProcessClick = (item) => {
    navigate("/data-injection/SubSubProcessModule", {
      state: {
        moduleName: moduleName,
        processName: item.name,
      },
    });
  };

  const getModuleIcon = (name, index) => {
    const icons = [
      React.createElement(FiDatabase, { className: "w-5 h-5" }),
      React.createElement(FiTrendingUp, { className: "w-5 h-5" }),
      React.createElement(FiShield, { className: "w-5 h-5" }),
      React.createElement(FiBarChart2, { className: "w-5 h-5" }),
      React.createElement(FiLayers, { className: "w-5 h-5" }),
      React.createElement(FiLock, { className: "w-5 h-5" }),
      React.createElement(FiActivity, { className: "w-5 h-5" }),
    ];
    return icons[index % icons.length];
  };

  const getCardGradient = (index) => {
    const gradients = [
      "from-blue-500 via-blue-600 to-indigo-600",
      "from-purple-500 via-purple-600 to-pink-600",
      "from-emerald-500 via-emerald-600 to-teal-600",
      "from-orange-500 via-orange-600 to-red-600",
      "from-rose-500 via-rose-600 to-pink-600",
      "from-indigo-500 via-indigo-600 to-purple-600",
      "from-cyan-500 via-cyan-600 to-blue-600",
    ];
    return gradients[index % gradients.length];
  };

  const getCardGlowColor = (index) => {
    const colors = [
      "shadow-blue-500/20",
      "shadow-purple-500/20",
      "shadow-emerald-500/20",
      "shadow-orange-500/20",
      "shadow-rose-500/20",
      "shadow-indigo-500/20",
      "shadow-cyan-500/20",
    ];
    return colors[index % colors.length];
  };

  const moduleImages = {
    PROCURE_TO_PAY: procureToPay,
    FORECAST_TO_DEPLOY: forecast,
    INFORMATION_TECHNOLOGY: informationTechnology,
    ORDER_TO_CASH: orderToCash,
    "GENERAL_ACCOUNTING_&_REPORTING": accounting,
    PROPERTY_PLANT_AND_EQUIPMENT: machine,
  };

  const getModuleImage = (name) => {
    const key = name?.toUpperCase().replace(/\s/g, "_");
    return moduleImages[key] || informationTechnology;
  };

  const getRandomMetric = (name) => {
    const metrics = [
      {
        label: "Efficiency",
        value: "98%",
        icon: React.createElement(FiZap, { className: "w-3 h-3" }),
      },
      {
        label: "Accuracy",
        value: "99.9%",
        icon: React.createElement(FiStar, { className: "w-3 h-3" }),
      },
      {
        label: "Performance",
        value: "2.3x",
        icon: React.createElement(FiCpu, { className: "w-3 h-3" }),
      },
    ];
    const hash = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return metrics[hash % metrics.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-indigo-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading modules...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-2xl p-6 text-red-600 shadow-lg">
          <div className="flex items-center gap-3">
            {React.createElement(FiShield, { className: "w-6 h-6" })}
            <span className="font-semibold">Error: {error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="relative bg-gradient-to-r from-[#36096d] via-[#4a0e8f] to-[#37d5d6] text-white rounded-2xl shadow-1xl border-b-4 border-[#37d5d6] overflow-hidden">
        {/* Animated gradient background with your project colors */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#36096d]/20 via-[#37d5d6]/10 to-[#36096d]/20 animate-pulse"></div>

        {/* Glass morphism effect */}
        <div className="absolute inset-0 backdrop-blur-[2px]"></div>

        <div className="relative p-3 z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl lg:text-2xl font-bold text-white bg-clip-text text-transparent">
                  Audit-Universe
                </h1>
              </div>
            </div>

            <button className="group flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#36096d] to-[#37d5d6] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-white/20">
              <svg
                className="w-4 h-4 group-hover:-translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Bottom shine effect with your theme colors */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#37d5d6] to-transparent"></div>
      </div>
      {/* Cards Grid */}
      <div className="">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mt-3">
          {Array.isArray(subModules) &&
            subModules.map((item, idx) => {
              const metric = getRandomMetric(item.name);
              const isHovered = hoveredCard === idx;

              return (
                <div
                  key={item.technicalName}
                  onClick={() => handleProcessClick(item)}
                  onMouseEnter={() => setHoveredCard(idx)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className={`group relative overflow-hidden rounded-3xl transition-all duration-500 cursor-pointer
                    ${isHovered ? "scale-105" : "scale-100"}`}
                >
                  {/* Gradient Border */}
                  <div
                    className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${getCardGradient(idx)} opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl ${getCardGlowColor(idx)}`}
                  ></div>

                  {/* Card Content */}
                  <div className="relative bg-gray-100 rounded-3xl shadow-xl overflow-hidden h-full transform transition-all duration-500 group-hover:shadow-2xl">
                    {/* Gradient Top Bar */}
                    <div
                      className={`h-2 bg-gradient-to-r ${getCardGradient(idx)}`}
                    ></div>

                    {/* Card Inner */}
                    <div className="p-6">
                      {/* Header with Icon and Image */}
                      <div className="flex items-start justify-between mb-6">
                        <div className="relative">
                          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 p-2 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                            <img
                              src={getModuleImage(item.name)}
                              alt={item.name}
                              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
                            />
                          </div>
                          {/* Floating Badge */}
                          <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                        {formatName(item.name)}
                      </h3>

                      {/* Action Button */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-sm font-semibold text-gray-600">
                          Explore Process
                        </span>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-800 to-gray-600 flex items-center justify-center text-white transform transition-all duration-300 group-hover:translate-x-1 group-hover:scale-110">
                          {React.createElement(FiChevronRight, {
                            className: "w-4 h-4",
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Hover Overlay Effect */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-r ${getCardGradient(idx)} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`}
                    ></div>

                    {/* Corner Accent */}
                    <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
                      <div
                        className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${getCardGradient(idx)} transform rotate-45 translate-x-10 -translate-y-10 group-hover:translate-x-6 group-hover:-translate-y-6 transition-transform duration-500`}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Empty State */}
        {(!subModules || subModules.length === 0) && !loading && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-6">
              {React.createElement(FiDatabase, {
                className: "w-12 h-12 text-gray-400",
              })}
            </div>
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">
              No Modules Available
            </h3>
            <p className="text-gray-500">
              No sub-modules found for this category
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubModulePage;
